import JSONPretty from 'react-json-pretty'
import React, { useCallback, useEffect, useState } from 'react'
import { getRouteHistoryList } from '@/lib/knots-api'
import { simulateMouseClick } from '@/utils/utils'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Puff } from '@/components/ui/Loaders'
import Datetime from 'react-datetime'
import 'react-datetime/css/react-datetime.css'
import './index.scss'
import { useTranslation } from 'react-i18next'
import Button from '@/components/ui/Buttons/Button'
import { useGetRoute } from '@/lib/react-query'

{
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
}

const HistoryItemData = (item: any) => {
  const [t] = useTranslation()
  return (
    <>
      <div className="row">
        <div className="col-4"> {t('historyData.label.invoked')}:</div>
        <div className="col-8">{item.invoked}</div>
      </div>
      <div className="row">
        <div className="col-4"> {t('historyData.label.error')}:</div>
        <div className="col-8">{item.error || 'None'}</div>
      </div>
      <div className="row">
        <div className="col-4"> {t('historyData.label.startedAt')}:</div>
        <div className="col-8">{item.startedAt}</div>
      </div>
      <div className="row">
        <div className="col-4"> {t('historyData.label.finishedAt')}:</div>
        <div className="col-8">{item.finishedAt}</div>
      </div>
      <div className="row">
        <div className="col-4"> {t('historyData.label.totalTime')}:</div>
        <div className="col-8">{item.totalTime}ms</div>
      </div>

      <div className="row">
        <div className="col-4"> {t('historyData.label.executionTime')}:</div>
        <div className="col-8">{item.data.executionTime}ms</div>
      </div>

      <div className="row">
        <div className="col-4"> {t('historyData.label.request')}:</div>
        <div className="col-8"></div>
      </div>
      <div className="row">
        <div className="col-1"></div>
        <div className="col-3"> {t('historyData.label.requestAsync')}:</div>
        <div className="col-8">{String(item.data.request?.async || 'false')}</div>
      </div>
      <div className="row">
        <div className="col-1"></div>
        <div className="col-3"> {t('historyData.label.requestConnectors')}:</div>
        <div className="col-8 history-item__list">
          {item.data.request?.connectors.map((c: any, i: number) => (
            <span key={i}>
              {c.slug} <br /> {c.url}{' '}
            </span>
          ))}
        </div>
      </div>
      <div className="row">
        <div className="col-1"></div>
        <div className="col-3"> {t('historyData.label.requestSecrets')}:</div>
        <div className="col-8">{item.data.request?.secrets.join(', ')}</div>
      </div>
      <div className="row">
        <div className="col-4"> {t('historyData.label.requestDetails')}:</div>
        <div className="col-8"></div>
      </div>
      <div className="row">
        <div className="col-12">
          {item.isLoggingEnabled ? (
            <JSONPretty data={item.data.request.request ?? {}} />
          ) : (
            'You need to enable logging in route settings to save body of the request in the history'
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-5"> {t('historyData.label.response')}:</div>
        <div className="col-7"></div>
      </div>
      <div className="row">
        <div className="col-1"></div>
        <div className="col-3"> {t('historyData.label.responseContentType')}:</div>
        <div className="col-8">{item.data.response?.contentType}</div>
      </div>
      <div className="row">
        <div className="col-1"></div>
        <div className="col-3"> {t('historyData.label.responseMessages')}:</div>
        <div className="col-8 history-item__list">
          {item.data.response?.messages?.map((m: any, i: number) => (
            <span key={i}>
              {m.type} <br /> {m.data}
            </span>
          ))}
        </div>
      </div>
    </>
  )
}

const HistoryItem = (props: any) => {
  return (
    <div className="history-item">
      <h5 onClick={props.open} className={'btn--list-item'}>
        {props.item.startedAt}
      </h5>

      <div className={'history-item__drawer ' + (props.isOpen ? 'history-item__drawer--open' : '')}>
        {/* {props.item && JSON.stringify(props.item)} */}
        {props.item && <HistoryItemData {...props.item} isLoggingEnabled={props.isLoggingEnabled} />}
      </div>
    </div>
  )
}

export function RouteHistoryList(props: any) {
  const [t] = useTranslation()
  const [tmpStartFrom, setTmpStartFrom] = useState<number | null>(null)
  const [startFrom, setStartFrom] = useState<number | null>(null)
  const [historyList, setHistoryList] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [openHistoryItem, setOpenHistoryItem] = useState<number | null>(null)
  const [showLoadMoreBtn, setShowLoadMoreBtn] = useState(true)
  const { data: route } = useGetRoute(props.projectId, props.routeId)
  const isLoggingEnabled = route?.route.loggingEnabled

  const refreshList = useCallback(
    async (time: null | number, append = false) => {
      setLoading(true)
      try {
        const r = await getRouteHistoryList(props.routeId, props.projectId, time)
        if (r) {
          const mapped = r.map((s: any) => {
            s.startedAt = new Date(s.data.started_at).toLocaleString('en-GB')
            s.finishedAt = new Date(s.stamp).toLocaleString('en-GB')
            s.totalTime = s.stamp - s.data.started_at
            return s
          })
          setHistoryList(append ? [...historyList, ...mapped] : mapped)
          setShowLoadMoreBtn(r.length === 10)
        }
      } finally {
        setLoading(false)
      }
    },
    [historyList]
  )

  useEffect(() => {
    refreshList(startFrom)
  }, [startFrom])

  return (
    <div className="history-list">
      <PerfectScrollbar>
        <div className="history-list__content">
          <div className="history-list__start-from knots-form">
            <label>{t('historyList.label.entriesBefore')}</label>
            <Datetime
              onChange={(m: any) => setTmpStartFrom((m.valueOf && m.valueOf()) || null)}
              inputProps={{ placeholder: t('historyList.label.enterDate') }}
              renderView={(viewMode: string, renderDefault: any) => {
                return (
                  <div className="wrapper">
                    {renderDefault()}
                    <div className="controls">
                      <button
                        className="btn"
                        onClick={() => {
                          setStartFrom(tmpStartFrom)
                          simulateMouseClick(document.querySelector('.history-list'))
                        }}
                      >
                        Apply selection
                      </button>
                    </div>
                  </div>
                )
              }}
            />
          </div>
          {historyList.map((historyItem: any, i: number) => (
            <HistoryItem
              key={i}
              projectId={props.projectId}
              isOpen={i == openHistoryItem}
              open={() => setOpenHistoryItem(i !== openHistoryItem ? i : null)}
              item={historyItem}
              isLoggingEnabled={isLoggingEnabled}
            />
          ))}
          {showLoadMoreBtn && (
            <Button
              disabled={loading}
              StartIcon={loading ? <Puff /> : undefined}
              onClick={() => historyList.length && refreshList(historyList[historyList.length - 1].stamp, true)}
            >
              {t('historyList.button.loadMore')}
            </Button>
          )}
        </div>
      </PerfectScrollbar>
    </div>
  )
}
